import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/api";
import FeaturedLoader from "../components/Loaders/FeaturedLoader";


export default function BlogDetail() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/blogs/${slug}`);
                setBlog(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        const fetchLatest = async () => {
            try {
                const res = await api.get("/blogs");
                setLatestBlogs(res.data);
            } catch (err) {
                console.error(err);
            }
        };

        fetchBlog();
        fetchLatest();
    }, [slug]);

    if (loading) {
        return (
            <FeaturedLoader
                title="Loading blog article"
                subtitle="Fetching the latest content for you..."
            />
        );
    }

    if (!blog) {
        return <p className="text-center py-20 text-gray-700 dark:text-gray-300">Blog not found</p>;
    }

    const currentUrl = window.location.href;

    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>
                    {blog.meta_title || `${blog.title} | UAE Dubai Hotels`}
                </title>

                <meta
                    name="description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        `Read ${blog.title} on UAE Dubai Hotels. Discover luxury hotels, budget stays, travel guides, Dubai attractions, and the latest hospitality updates across the UAE.`
                    }
                />

                {blog.meta_keywords && (
                    <meta
                        name="keywords"
                        content={blog.meta_keywords}
                    />
                )}

                <meta name="robots" content="index, follow" />
                <meta name="author" content="UAE Dubai Hotels" />

                {/* Canonical */}
                <link
                    rel="canonical"
                    href={`https://uaedubaihotels.com/blogs/${blog.slug}`}
                />

                {/* Open Graph */}
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="UAE Dubai Hotels" />
                <meta property="og:locale" content="en_US" />

                <meta
                    property="og:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    property="og:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        `Read ${blog.title} on UAE Dubai Hotels.`
                    }
                />

                <meta
                    property="og:url"
                    content={`https://uaedubaihotels.com/blogs/${blog.slug}`}
                />

                <meta
                    property="og:image"
                    content={
                        blog?.image
                            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
                            : "https://uaedubaihotels.com/images/default-og.png"
                    }
                />

                {/* Article Meta */}
                {blog.created_at && (
                    <meta
                        property="article:published_time"
                        content={blog.created_at}
                    />
                )}

                {blog.updated_at && (
                    <meta
                        property="article:modified_time"
                        content={blog.updated_at}
                    />
                )}

                {/* Twitter */}
                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    name="twitter:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        `Read ${blog.title} on UAE Dubai Hotels.`
                    }
                />

                <meta
                    name="twitter:image"
                    content={
                        blog?.image
                            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
                            : "https://uaedubaihotels.com/images/default-og.png"
                    }
                />
            </Helmet>

            <div className="bg-[#F8F8F8] dark:bg-[#030712] text-gray-900 dark:text-white">

                {/* ================= HERO ================= */}

                <section className="relative min-h-[50vh] flex items-end py-8">

                    <img
                        src={`https://uaediscountedhotels.com/uploads/blogs/banners/${blog.banner_image}`}
                        alt={blog.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />

                    {/* Overlay */}

                    <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-black/60 to-black/30" />

                    {/* Decorative Blur */}

                    {/* <div className="absolute top-24 right-20 w-72 h-72 bg-[#D9BB74]/20 blur-[120px] rounded-full"></div> */}

                    <div className="container mx-auto px-6 relative z-10 pb-20">

                        {/* Breadcrumb */}

                        <div className="flex flex-wrap items-center gap-2 text-sm text-white/70 mb-8">

                            <Link
                                to="/"
                                className="hover:text-[#D9BB74] transition"
                            >
                                Home
                            </Link>

                            <span>/</span>

                            <Link
                                to="/blogs"
                                className="hover:text-[#D9BB74] transition"
                            >
                                Blogs
                            </Link>

                            <span>/</span>

                            <span className="text-white line-clamp-1">

                                {blog.title}

                            </span>

                        </div>

                        {/* Category */}

                        <span className="
inline-flex
items-center
gap-2
px-5
py-2
rounded-full
bg-[#D9BB74]
text-black
font-semibold
tracking-wide
uppercase
text-xs
shadow-xl
">

                            ✦ {blog.category?.name || "Travel"}

                        </span>

                        {/* Title */}

                        <h1
                            className="
        mt-8
        text-xl
        sm:text-2xl
        md:text-4xl
        lg:text-5xl
        font-bold
        leading-tight
        text-white
    "
                        >
                            {blog.title}
                        </h1>

                        {/* Excerpt */}

                        {/* {blog.excerpt && (

                            <p className="
mt-8
text-lg
leading-8
text-white/75
">

                                {blog.excerpt}

                            </p>

                        )} */}

                        {/* Info Bar */}

                        <div className="
mt-10
flex
flex-wrap
items-center
gap-8
">

                            <div>

                                <p className="text-white/50 text-xs uppercase tracking-widest">

                                    Published

                                </p>

                                <p className="mt-1 font-semibold text-white">

                                    {new Date(blog.created_at).toDateString()}

                                </p>

                            </div>

                            <div className="w-px h-12 bg-white/20 hidden md:block"></div>

                            <div>

                                <p className="text-white/50 text-xs uppercase tracking-widest">

                                    Category

                                </p>

                                <p className="mt-1 font-semibold text-white">

                                    {blog.category?.name || "Travel"}

                                </p>

                            </div>

                            <div className="w-px h-12 bg-white/20 hidden md:block"></div>

                            <div>

                                <p className="text-white/50 text-xs uppercase tracking-widest">

                                    Reading Time

                                </p>

                                <p className="mt-1 font-semibold text-white">

                                    {Math.max(
                                        2,
                                        Math.ceil(
                                            blog.content.replace(/<[^>]+>/g, "").split(" ").length / 200
                                        )
                                    )} min read

                                </p>

                            </div>

                        </div>

                    </div>

                </section>

                {/* ================= CONTENT ================= */}

                <section className="container mx-auto px-6 -mt-16 relative z-20">

                    <div className="grid lg:grid-cols-12 gap-10 items-start">

                        {/* ================= ARTICLE ================= */}

                        <article className="lg:col-span-8">

                            <div className="
bg-white
dark:bg-[#111827]
rounded-[35px]
shadow-2xl
border
border-gray-200
dark:border-gray-800
overflow-hidden
">

                                {/* Floating Info */}

                                <div className="
flex
flex-wrap
justify-between
items-center
gap-4
px-8
py-6
border-b
border-gray-200
dark:border-gray-800
">

                                    <div className="flex items-center gap-3">

                                        <div className="
w-14
h-14
rounded-full
bg-[#D9BB74]
flex
items-center
justify-center
font-bold
text-xl
text-black
">

                                            ✦

                                        </div>

                                        <div>

                                            <p className="text-xs uppercase tracking-widest text-gray-500">

                                                UAE Travel Journal

                                            </p>

                                            <h3 className="font-bold text-lg dark:text-white">

                                                Premium Travel Guide

                                            </h3>

                                        </div>

                                    </div>

                                    <div className="
px-5
py-2
rounded-full
bg-[#D9BB74]/10
text-[#D9BB74]
font-semibold
">

                                        {blog.category?.name}

                                    </div>

                                </div>

                                {/* BLOG CONTENT STARTS BELOW */}

                                <div>

                                    {/* ================= MAIN ================= */}
                                    <div className="lg:col-span-2">

                                        {/* ================= ARTICLE CONTENT ================= */}

                                        <div className="px-8 lg:px-12 py-10">

                                            {blog.excerpt && (
                                                <div className="
        mb-10
        p-8
        rounded-3xl
        bg-[#D9BB74]/10
        border
        border-[#D9BB74]/20
        italic
        text-lg
        leading-8
        text-gray-700
        dark:text-gray-300
    ">
                                                    {blog.excerpt}
                                                </div>
                                            )}

                                            <div
                                                className="
        prose
        prose-lg
        dark:prose-invert
        max-w-none

        prose-headings:font-bold
        prose-headings:text-gray-900
        dark:prose-headings:text-white

        prose-p:text-gray-700
        dark:prose-p:text-gray-300

        prose-p:leading-8

        prose-a:text-[#D9BB74]

        prose-strong:text-gray-900
        dark:prose-strong:text-white

        prose-img:rounded-3xl
        prose-img:shadow-xl

        prose-blockquote:border-l-4
        prose-blockquote:border-[#D9BB74]
        prose-blockquote:bg-[#D9BB74]/10
        prose-blockquote:px-6
        prose-blockquote:py-3
        prose-blockquote:rounded-r-2xl

        prose-ul:marker:text-[#D9BB74]
        prose-ol:marker:text-[#D9BB74]
    "
                                                dangerouslySetInnerHTML={{
                                                    __html: blog.content,
                                                }}
                                            />

                                            {/* ================= ARTICLE FOOTER ================= */}

                                            <div className="
    mt-14
    pt-10
    border-t
    border-gray-200
    dark:border-gray-800
">

                                                <div className="
        flex
        flex-col
        lg:flex-row
        justify-between
        gap-10
">

                                                    {/* Author */}

                                                    <div className="flex items-center gap-5">

                                                        <div className="
                w-20
                h-20
                rounded-full
                bg-[#D9BB74]
                flex
                items-center
                justify-center
                text-3xl
                font-bold
                text-black
            ">
                                                            ✦
                                                        </div>

                                                        <div>

                                                            <p className="text-sm text-gray-500">

                                                                Written by

                                                            </p>

                                                            <h3 className="text-xl font-bold dark:text-white">

                                                                UAE Discounted Hotels

                                                            </h3>

                                                            <p className="mt-1 text-gray-500">

                                                                Luxury Travel & Hotel Experts

                                                            </p>

                                                        </div>

                                                    </div>

                                                    {/* Published */}

                                                    <div className="
            flex
            flex-wrap
            gap-8
        ">

                                                        <div>

                                                            <p className="text-sm text-gray-500">

                                                                Published

                                                            </p>

                                                            <h4 className="font-semibold mt-1 dark:text-white">

                                                                {new Date(blog.created_at).toDateString()}

                                                            </h4>

                                                        </div>

                                                        <div>

                                                            <p className="text-sm text-gray-500">

                                                                Category

                                                            </p>

                                                            <h4 className="font-semibold mt-1 dark:text-white">

                                                                {blog.category?.name}

                                                            </h4>

                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                            {/* ================= SHARE ================= */}

                                            <div className="mt-16">

                                                <h3 className="
        text-2xl
        font-bold
        dark:text-white
        mb-8
    ">

                                                    Share This Article

                                                </h3>

                                                <div className="
        grid
        grid-cols-2
        md:grid-cols-3
        lg:grid-cols-6
        gap-4
    ">

                                                    <a
                                                        href={`https://wa.me/?text=${encodeURIComponent(currentUrl)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="
                h-14
                rounded-2xl
                bg-green-500
                hover:scale-105
                transition
                text-white
                font-semibold
                flex
                items-center
                justify-center
            "
                                                    >
                                                        WhatsApp
                                                    </a>

                                                    <a
                                                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="
                h-14
                rounded-2xl
                bg-blue-600
                hover:scale-105
                transition
                text-white
                font-semibold
                flex
                items-center
                justify-center
            "
                                                    >
                                                        Facebook
                                                    </a>

                                                    <a
                                                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="
                h-14
                rounded-2xl
                bg-black
                hover:scale-105
                transition
                text-white
                font-semibold
                flex
                items-center
                justify-center
            "
                                                    >
                                                        X
                                                    </a>

                                                    <a
                                                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="
                h-14
                rounded-2xl
                bg-sky-700
                hover:scale-105
                transition
                text-white
                font-semibold
                flex
                items-center
                justify-center
            "
                                                    >
                                                        LinkedIn
                                                    </a>

                                                    <a
                                                        href={`https://t.me/share/url?url=${encodeURIComponent(currentUrl)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="
                h-14
                rounded-2xl
                bg-cyan-500
                hover:scale-105
                transition
                text-white
                font-semibold
                flex
                items-center
                justify-center
            "
                                                    >
                                                        Telegram
                                                    </a>

                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(currentUrl);
                                                            alert("Link copied successfully.");
                                                        }}
                                                        className="
                h-14
                rounded-2xl
                bg-[#D9BB74]
                hover:bg-[#caa960]
                transition
                text-black
                font-bold
            "
                                                    >
                                                        Copy Link
                                                    </button>

                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                    {/* ================= SIDEBAR STARTS BELOW */}

                                    {/* ================= LUXURY SIDEBAR ================= */}



                                </div>

                            </div>

                        </article>

                        {/* ================= SIDEBAR ================= */}

                        <aside className="lg:col-span-4">

                            <div className="space-y-8">

                                {/* ================= ABOUT CARD ================= */}

                                <div className="
        bg-white
        dark:bg-[#111827]
        rounded-[32px]
        shadow-xl
        border
        border-gray-200
        dark:border-gray-800
        overflow-hidden
    ">

                                    <div className="
            bg-gradient-to-r
            from-[#D9BB74]
            to-[#CDA85A]
            p-8
            text-black
        ">

                                        <p className="uppercase tracking-[0.25em] text-xs font-bold">

                                            UAE TRAVEL

                                        </p>

                                        <h3 className="text-3xl font-bold mt-3">

                                            Travel Journal

                                        </h3>

                                    </div>

                                    <div className="p-8">

                                        <p className="text-gray-600 dark:text-gray-400 leading-8">

                                            Discover luxury hotels, hidden destinations,
                                            travel guides and exclusive accommodation tips
                                            across Dubai, Abu Dhabi and the UAE.

                                        </p>

                                        <Link
                                            to="/blogs"
                                            className="
                    mt-8
                    inline-flex
                    items-center
                    justify-center
                    w-full
                    h-14
                    rounded-2xl
                    bg-[#D9BB74]
                    text-black
                    font-bold
                    hover:scale-[1.03]
                    transition
                "
                                        >

                                            Explore All Articles →

                                        </Link>

                                    </div>

                                </div>


                                {/* ================= LATEST ARTICLES ================= */}

                                <div className="lg:col-span-4">
                                    <div className="sticky top-24">
                                        <div
                                            className="
                bg-white
                dark:bg-[#111827]
                rounded-[32px]
                shadow-xl
                border
                border-gray-200
                dark:border-gray-800
                p-8
                space-y-10
            "
                                        >

                                            <div className="flex items-center justify-between mb-8">

                                                <div>

                                                    <p className="uppercase text-xs tracking-[0.3em] text-[#D9BB74]">

                                                        Recent

                                                    </p>

                                                    <h3 className="text-2xl font-bold dark:text-white mt-2">

                                                        Latest Articles

                                                    </h3>

                                                </div>

                                                <div className="
                w-12
                h-12
                rounded-full
                bg-[#D9BB74]/10
                flex
                items-center
                justify-center
                text-[#D9BB74]
                text-xl
            ">
                                                    ✦
                                                </div>

                                            </div>

                                            <div className="space-y-6">

                                                <div className="space-y-6 max-h-[420px] overflow-y-auto pr-2">
                                                    {latestBlogs
                                                        .filter(item => item.slug !== blog.slug)
                                                        .slice(0, 5)
                                                        .map((item) => (

                                                            <Link
                                                                key={item.id}
                                                                to={`/blogs/${item.slug}`}
                                                                className="
                            group
                            flex
                            gap-4
                            items-center
                        "
                                                            >

                                                                <div className="
                            w-24
                            h-24
                            rounded-2xl
                            overflow-hidden
                            shrink-0
                        ">

                                                                    <img
                                                                        src={item.thumbnail_image}
                                                                        alt={item.title}
                                                                        className="
                                    w-full
                                    h-full
                                    object-cover
                                    group-hover:scale-110
                                    duration-500
                                "
                                                                    />

                                                                </div>

                                                                <div className="flex-1">

                                                                    <span className="
                                text-xs
                                text-[#D9BB74]
                                uppercase
                                tracking-wider
                            ">

                                                                        {item.category?.name}

                                                                    </span>

                                                                    <h4 className="
                                mt-2
                                font-bold
                                text-gray-900
                                dark:text-white
                                line-clamp-2
                                group-hover:text-[#D9BB74]
                                transition
                            ">

                                                                        {item.title}

                                                                    </h4>

                                                                    <p className="
                                mt-2
                                text-xs
                                text-gray-500
                            ">

                                                                        {new Date(item.created_at).toDateString()}

                                                                    </p>

                                                                </div>

                                                            </Link>

                                                        ))}

                                                </div>

                                            </div>

                                            {/* ================= NEWSLETTER ================= */}

                                            <div className="
        relative
        overflow-hidden
        rounded-[32px]
        bg-gradient-to-br
        from-[#0F172A]
        via-[#111827]
        to-[#1F2937]
        p-8
        text-white
        shadow-2xl
        mt-10
    ">

                                                <div className="
            absolute
            -right-16
            -top-16
            w-52
            h-52
            rounded-full
            bg-[#D9BB74]/20
            blur-3xl
        "></div>

                                                <div className="relative rounded-[32px]">

                                                    <span className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-[#D9BB74]
                text-black
                text-xs
                uppercase
                font-bold
                tracking-wider
            ">

                                                        Stay Updated

                                                    </span>

                                                    <h3
                                                        className="
        mt-6
        text-2xl
        sm:text-3xl
        font-bold
        leading-tight
    "
                                                    >
                                                        Explore More UAE Travel Stories
                                                    </h3>
                                                    <p className="
                mt-5
                text-gray-300
                leading-8
            ">

                                                        Browse luxury hotels,
                                                        destination guides,
                                                        hidden gems and
                                                        exclusive travel experiences.

                                                    </p>

                                                    <Link
                                                        to="/blogs"
                                                        className="
                    mt-8
                    inline-flex
                    items-center
                    justify-center
                    w-full
                    h-14
                    rounded-2xl
                    bg-[#D9BB74]
                    text-black
                    font-bold
                    hover:bg-white
                    transition
                "
                                                    >

                                                        View All Blogs →

                                                    </Link>

                                                </div>

                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>

                        </aside>

                    </div>

                </section>

            </div>
        </>
    );
}